import { lazy, Suspense, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { HiOutlineX } from 'react-icons/hi'
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { isRtl, useLocale } from '@/lib/locale'
import { copy } from './Slide07SpotAi.copy'

// Lazy-loaded so Recharts (~100KB) stays out of the main bundle. Chart
// only fetches when a learner actually reaches this slide.
const ScamLossChart = lazy(() => import('@/components/ScamLossChart'))

const ease = [0.16, 1, 0.3, 1] as const
const transitionBase = { duration: 0.45, ease }
const animationProps = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } }

export default function Slide07SpotAi(_props: SlideProps) {
  const c = useSlideCopy(copy)
  const [panelOpen, setPanelOpen] = useState(false)

  // Split body around the {trigger} sentinel so the phrase renders as an
  // inline dotted-underline button that opens the right-side slideover panel.
  const [beforeTrigger, afterTrigger] = c.body.split('{trigger}')

  // ESC to close the panel.
  useEffect(() => {
    if (!panelOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panelOpen])

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center">
      <div className="w-full max-w-4xl mx-auto">
        {/* Title (left-aligned) */}
        <motion.div
          {...animationProps}
          transition={{ ...transitionBase, delay: 0 }}
          className="mb-5"
        >
          <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1]">
            {c.title}
          </h2>
        </motion.div>

        <div className="min-h-0 flex flex-col space-y-6">
          <div className="text-xl sm:text-2xl text-slate-600 leading-relaxed mb-6">
            {beforeTrigger}
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="text-inherit underline decoration-dotted decoration-slate-400 underline-offset-4 hover:decoration-violet-400 hover:text-slate-900 transition-colors cursor-pointer"
            >
              {c.triggerText}
            </button>
            {afterTrigger}
          </div>
          <div className="mt-4 min-h-0 flex flex-col w-full">
            <div className="mt-0 min-w-0 w-full">
              <Suspense
                fallback={
                  <div
                    aria-hidden
                    className="h-[320px] w-full animate-pulse rounded-2xl bg-violet-50/40"
                  />
                }
              >
                <ScamLossChart />
              </Suspense>
            </div>
            <p className="mt-6 text-center text-sm text-slate-400">{c.sources}</p>
          </div>
        </div>
      </div>

      <InsidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        eyebrow={c.panelEyebrow}
        title={c.panelTitle}
        intro={c.panelIntro}
        beats={[
          { label: c.panelBeat1Label, body: c.panelBeat1Body },
          { label: c.panelBeat2Label, body: c.panelBeat2Body },
          { label: c.panelBeat3Label, body: c.panelBeat3Body },
          { label: c.panelBeat4Label, body: c.panelBeat4Body },
          { label: c.panelBeat5Label, body: c.panelBeat5Body },
        ]}
        closeLabel={c.panelClose}
      />
    </section>
  )
}

/* ─── Right-side slideover panel ─────────────────────────────── */

type Beat = { label: string; body: string }

type InsidePanelProps = {
  open: boolean
  onClose: () => void
  eyebrow: string
  title: string
  intro: string
  beats: Beat[]
  closeLabel: string
}

function InsidePanel({ open, onClose, eyebrow, title, intro, beats, closeLabel }: InsidePanelProps) {
  // The panel is pinned to the inline end (`end-0`), so it must slide in from
  // the left edge under RTL. Framer's `x` is physical, hence the sign flip.
  const offscreen = isRtl(useLocale()) ? '-100%' : '100%'
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="inside-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-slate-900/40"
            aria-hidden="true"
          />
          <motion.aside
            key="inside-panel"
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ x: offscreen }}
            animate={{ x: 0 }}
            exit={{ x: offscreen }}
            transition={{ duration: 0.45, ease }}
            className="fixed top-0 end-0 z-[60] h-full w-full sm:max-w-lg bg-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.4)] flex flex-col"
          >
            <header className="flex items-center justify-between px-6 sm:px-8 pt-5 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="shrink-0 flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600 ring-1 ring-violet-100">
                  <HiOutlineBuildingOffice2 className="h-5 w-5" />
                </div>
                <div className="min-w-0 leading-tight">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-violet-500 font-semibold">
                    {eyebrow}
                  </p>
                  <h3 className="mt-0.5 text-lg sm:text-xl font-semibold text-slate-900 leading-tight truncate">
                    {title}
                  </h3>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 ms-4 flex h-9 w-9 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                aria-label={closeLabel}
              >
                <HiOutlineX className="h-5 w-5" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-7">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                {intro}
              </p>

              <div className="mt-6 space-y-5">
                {beats.map((beat, i) => (
                  <motion.div
                    key={beat.label}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.12 + i * 0.08, ease }}
                  >
                    <h4 className="text-lg sm:text-xl font-semibold text-slate-900 leading-snug">
                      {beat.label}
                    </h4>
                    <p className="mt-1 text-base sm:text-lg text-slate-600 leading-relaxed">
                      {beat.body}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}
